import type { PerformanceClinic } from '@/entities/performance/api/performance.api';
import { usePerformanceClinics } from '@/entities/performance/hooks/usePerformanceQueries';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { cn } from '@/shared/lib/utils';
import { toApiYm } from '@/shared/utils/format';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Icon } from '@/shared/components/ui/icon';
import { useMemo, useRef, useState } from 'react';
import { ClinicList } from './components/ClinicList';
import { ClinicSummary } from './components/ClinicSummary';
import { ImagesTab } from './components/ImagesTab';
import { MonthHeader } from './components/MonthHeader';
import { DrugsTab, PharmaTab } from './components/PrescriptionTabs';

type PerformanceTab = 'all' | 'images' | 'pharma';

const TABS: { value: PerformanceTab; label: string }[] = [
  { value: 'all', label: '의약품' },
  { value: 'images', label: '처방 이미지' },
  { value: 'pharma', label: '제약사' },
];

/**
 * M-09 월 실적 · 병의원 상세 (+ M-16 처방 이미지 탭, M-17 제약사 탭) — Figma 712:1287 · 735:5498 · 789:10041
 * 대시보드(M-08) 선택 월 표의 행에서 들어온다 (월 카드 · x축 라벨 진입은 폐기).
 * yearMonth는 URL에서 YYYY-MM(정산월), API 호출 시 yyyyMM으로 바꾼다.
 * 탭 값 all은 주소 호환을 위해 그대로 두고 라벨만 「의약품」이다.
 * 선택 병의원(clinicId) · 탭(tab)은 URL에 둔다.
 * 모바일(<1024)은 목록 → 상세 2단계: clinicId가 URL에 있으면 상세만 보여준다 (README §8).
 */
export const PerformanceMonthPage = () => {
  const navigate = useNavigate();
  const { yearMonth = '' } = useParams({ strict: false }) as {
    yearMonth?: string;
  };
  const search = useSearch({ strict: false }) as {
    clinicId?: number;
    tab?: PerformanceTab;
  };
  const ym = toApiYm(yearMonth);
  const tab = search.tab ?? 'all';

  const [keyword, setKeyword] = useState('');
  const clinicName = useDebouncedValue(keyword.trim());
  const stats = usePerformanceClinics(ym, clinicName);
  // 서버 정렬(예상↓ → 확정↓) 그대로 — 목록 표시와 기본 선택이 같은 순서를 쓴다
  const clinics = useMemo(
    () => stats.data?.pages.flatMap((p) => p.content) ?? [],
    [stats.data],
  );
  const totalCount = stats.data?.pages[0]?.totalCount;
  // 검색으로 선택한 병의원이 목록에서 빠져도 이름 · 거래처 ID를 잃지 않게 본 적 있는 병의원을 기억한다
  const seen = useRef(new Map<string, PerformanceClinic>());
  clinics.forEach((c) => seen.current.set(`${ym}-${c.clinicId}`, c));
  // 데스크톱은 URL에 병의원이 없으면 첫 번째 병의원을 보여준다
  const clinicId = search.clinicId ?? clinics[0]?.clinicId;
  const clinic =
    clinicId === undefined ? undefined : seen.current.get(`${ym}-${clinicId}`);
  const noClinics = stats.isSuccess && !clinicName && totalCount === 0;

  const setSearch = (next: { clinicId?: number; tab?: PerformanceTab }) =>
    void navigate({
      to: ROUTE_PATHS.PERFORMANCE_MONTH,
      params: { yearMonth },
      search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
      replace: next.tab !== undefined,
    });

  const validYm = /^\d{6}$/.test(ym);
  if (!validYm) {
    return <EmptyState message="잘못된 주소예요." />;
  }

  return (
    <div className="flex flex-col gap-4">
      <MonthHeader ym={ym} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <ClinicList
          ym={ym}
          clinics={clinics}
          loading={stats.isPending}
          keyword={keyword}
          onKeywordChange={setKeyword}
          hasNextPage={!!stats.hasNextPage}
          isFetchingNextPage={stats.isFetchingNextPage}
          fetchNextPage={() => stats.fetchNextPage()}
          selectedId={clinicId}
          onSelect={(id) => setSearch({ clinicId: id })}
          className={cn(search.clinicId !== undefined && 'hidden lg:flex')}
        />

        <div
          className={cn(
            'flex min-w-0 flex-col gap-4',
            search.clinicId === undefined && 'hidden lg:flex',
          )}
        >
          {/* 모바일 상세 단계에서만 보이는 [‹ 병의원 목록] */}
          <button
            type="button"
            onClick={() => setSearch({ clinicId: undefined })}
            className="flex items-center gap-1 self-start text-button-14-sb text-fg-tertiary lg:hidden"
          >
            <Icon name="nav-arrow-left-line-16" />
            병의원 목록
          </button>

          {stats.isError ? (
            <ErrorState onRetry={() => void stats.refetch()} />
          ) : noClinics ? (
            <EmptyState
              className="rounded-20 bg-surface-white py-20"
              message="이 달에 실적이 있는 병의원이 없어요."
            />
          ) : clinicId === undefined ? null : (
            <>
              <ClinicSummary
                ym={ym}
                clinicId={clinicId}
                clinic={clinic}
                clinicLoading={stats.isPending}
              />
              <section className="rounded-20 bg-surface-white pb-6">
                <Tabs
                  value={tab}
                  onValueChange={(v) => setSearch({ tab: v as PerformanceTab })}
                >
                  <TabsList className="mb-8 rounded-t-20 pt-2">
                    {TABS.map((t) => (
                      <TabsTrigger
                        key={t.value}
                        value={t.value}
                        className="first:ml-4"
                      >
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {/* key: 병의원 · 월이 바뀌면 탭 안의 검색 · 필터 상태를 초기화한다 */}
                  <TabsContent value="all" className="outline-none">
                    <DrugsTab
                      key={`${ym}-${clinicId}`}
                      ym={ym}
                      clinicId={clinicId}
                      clinicName={clinic?.clinicName}
                    />
                  </TabsContent>
                  <TabsContent value="images" className="outline-none">
                    <ImagesTab
                      key={`${ym}-${clinicId}`}
                      ym={ym}
                      clinicId={clinicId}
                      clinicName={clinic?.clinicName ?? ''}
                      relationshipId={clinic?.relationshipId ?? null}
                    />
                  </TabsContent>
                  <TabsContent value="pharma" className="outline-none">
                    <PharmaTab
                      key={`${ym}-${clinicId}`}
                      ym={ym}
                      clinicId={clinicId}
                      clinicName={clinic?.clinicName}
                    />
                  </TabsContent>
                </Tabs>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
